// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo } from "react";
import { LuSearch, LuCloudOff } from "react-icons/lu";
import { RxCross2 } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getSession } from "../../utils/sessionstore";
import { handleItem } from "../../utils/handleItem";
import { logAmplitudeEvent } from "../../utils/amplitude/functions";
import amplitude from "../../utils/amplitude/initiate";

const SearchBar = ({ afterLandDashboard }) => {
  const { subscriptions, userDetails, clientname } = useSelector((state) => ({
    subscriptions: state.subscriptions,
    userDetails: state.userDetails,
    clientname: state.clientname,
  }));
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { clientID } = useParams();

  const result = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return [];

    const seen = new Set();
    const matches = [];
    for (const subscription of subscriptions || []) {
      for (const category of subscription.productcategoryIds || []) {
        for (const dashboard of category.dashboardIds || []) {
          if (!dashboard?.label?.toLowerCase().includes(needle)) continue;
          if (seen.has(dashboard._id)) continue;
          seen.add(dashboard._id);
          matches.push({ dashboard, productLine: subscription.label });
        }
      }
    }
    return matches;
  }, [subscriptions, searchTerm]);

  async function landDashboardNew(dashboard, productLine) {
    await handleItem({
      productLine: productLine,
      clientID: clientID ?? getSession("client_id"),
      dashboard: dashboard,
      dispatch: dispatch,
      navigate: navigate,
      userDetails: userDetails,
      clientName: clientname,
      pathname: location.pathname,
      triggerType: "SEARCH_DASHBOARD",
    });

    if (afterLandDashboard) afterLandDashboard();
  }
  



  return (
    <>
      <div className="font-geist relative w-full flex flex-col justify-start items-start gap-1">
        <div className="self-stretch relative">
          <LuSearch className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-general-muted-foreground" />
          <input
            type="search"
            className="flex h-9 w-full rounded-md border border-general-border bg-general-input pl-8 pr-8 text-sm font-normal leading-5 text-general-foreground shadow-sm outline-none placeholder:text-general-muted-foreground [&::-webkit-search-cancel-button]:appearance-none"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            onFocus={() =>
              logAmplitudeEvent(amplitude, "Search", {
                eventTimestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
              })
            }
          />
          {searchTerm.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-general-muted-foreground hover:bg-general-accent ani"
            >
              <RxCross2 className="size-3.5" />
            </button>
          )}
        </div>
        {searchTerm.trim().length > 0 && (
          <div className="app-scrollbar self-stretch max-h-64 p-0.5 bg-card-card rounded-lg shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.10)] shadow-md outline outline-1 outline-general-border flex flex-col justify-start items-start overflow-y-auto overscroll-contain">
            {result.length > 0 ? (
              result.map(({ dashboard, productLine }) => (
                <p
                  key={dashboard._id}
                  className="self-stretch min-h-8 px-2 py-1.5 rounded-md hover:bg-general-accent ani cursor-pointer truncate text-left text-general-foreground text-sm font-normal leading-5"
                  onClick={() => {
                    landDashboardNew(dashboard, productLine);
                  }}
                >
                  {dashboard.label}
                </p>
              ))
            ) : (
              <div className="self-stretch py-4 flex flex-col justify-start items-center gap-4">
                <div className="size-10 p-2 bg-general-secondary rounded-lg flex justify-center items-center">
                  <LuCloudOff className="size-6 text-general-primary" />
                </div>
                <div className="flex flex-col justify-start items-center gap-1">
                  <div className="self-stretch text-center text-general-primary text-base font-medium leading-6">
                    {"No data to display"}
                  </div>
                  <div className="text-center text-general-muted-foreground text-sm font-normal leading-5">
                    {"Try changing the name and search again."}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="min-h-9 px-4 py-2 bg-general-input rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-general-border hover:bg-[rgba(0,0,0,0.05)] ani flex justify-center items-center gap-2"
                >
                  <span className="text-center text-general-foreground text-sm font-medium leading-5">
                    {"Clear Search"}
                  </span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default memo(SearchBar);
